import { ENTITIES, MODULES, REPORTS, ROOT_PARAMETRES_ENTITIES, ROOT_PARAMETRES_MODULES } from '~/data/maquette'

/** `currentView` de la maquette : {type, key, id}. Ici elle se déduit de l'adresse de la page. */
export interface View {
  type: string
  key?: string
  id?: string
}

export function viewPath(type: string, key?: string, id?: string | number): string {
  switch (type) {
    case 'guide': return '/guide'
    case 'module': return `/module/${key}`
    case 'group': return `/group/${key}/${id}`
    case 'entity': return `/entity/${key}`
    case 'detail': return `/entity/${key}/${id}`
    case 'report': return `/report/${key}`
    case 'wizard': return '/wizard'
    case 'relanceBulk': return '/relance-groupee'
    case 'recu-inscription': return '/recu-inscription'
    case 'relance':
    case 'attestation':
    case 'liste-classe':
    case 'releve-notes':
    case 'recu-print': return `/${type}/${key}/${id}`
    default: return '/'
  }
}

export function useCurrentView() {
  const route = useRoute()
  return computed<View>(() => {
    const p = route.path.split('/').filter(Boolean)
    if (p[0] === 'guide') return { type: 'guide' }
    if (p[0] === 'module') return { type: 'module', key: p[1] }
    if (p[0] === 'group') return { type: 'group', key: p[1], id: p[2] }
    if (p[0] === 'entity') return p[2] ? { type: 'detail', key: p[1], id: p[2] } : { type: 'entity', key: p[1] }
    if (p[0] === 'report') return { type: 'report', key: p[1] }
    if (p[0] === 'wizard') return { type: 'wizard' }
    if (p[0] === 'relance-groupee') return { type: 'relanceBulk' }
    if (p[0] === 'recu-inscription') return { type: 'recu-inscription' }
    if (['relance', 'attestation', 'liste-classe', 'releve-notes', 'recu-print'].includes(p[0]!)) return { type: p[0]!, key: p[1], id: p[2] }
    return { type: 'dashboard' }
  })
}

/** Menu déplié, groupe déplié, menu réduit (bureau) ou ouvert (mobile). */
export function useNav() {
  return {
    expandedModule: useState<string | null>('expandedModule', () => null),
    expandedGroup: useState<string | null>('expandedGroup', () => null),
    navOpen: useState('navOpen', () => false),
    collapsed: useState('collapsed', () => false),
  }
}

function computeModuleForView(type: string, key?: string): string | null {
  if (type === 'module' || type === 'group') return key ?? null
  if (type === 'entity' || type === 'detail') return (key && ENTITIES[key]?.module) || null
  if (type === 'report') return (key && REPORTS[key]?.module) || null
  return null
}

function findGroupForEntity(ek: string): string | null {
  for (const mk of Object.keys(MODULES)) {
    for (const g of MODULES[mk]!.groups) {
      if (g.entities.includes(ek)) return `${mk}:${g.key}`
    }
  }
  return null
}

/** Dépliage du menu selon la vue : la logique de `goTo` de la maquette. */
export function applyExpansion(type: string, key?: string, id?: string | number): void {
  const { expandedModule, expandedGroup } = useNav()
  const modKey = computeModuleForView(type, key)
  const root = '__root_parametres__'
  if (type === 'group' && key === 'finance' && id === 'parametres') {
    expandedModule.value = root
    expandedGroup.value = 'finance:parametres'
  }
  else if ((type === 'entity' || type === 'detail') && key && ROOT_PARAMETRES_ENTITIES.includes(key)) {
    expandedModule.value = root
  }
  else if (modKey && ROOT_PARAMETRES_MODULES.includes(modKey)) {
    expandedModule.value = root
    expandedGroup.value = `${root}:${modKey}`
  }
  else if (type === 'group') {
    expandedModule.value = modKey
    expandedGroup.value = `${modKey}:${id}`
  }
  else if (modKey) {
    expandedModule.value = modKey
    if ((type === 'entity' || type === 'detail') && key) {
      const grp = findGroupForEntity(key)
      if (grp) {
        if (grp === 'finance:parametres') {
          expandedModule.value = root
          expandedGroup.value = grp
        }
        else {
          expandedGroup.value = grp
        }
      }
    }
  }
}

export function goTo(type: string, key?: string, id?: string | number): void {
  useNav().navOpen.value = false
  if (type === 'entity' && key) resetTable(key)
  applyExpansion(type, key, id)
  navigateTo(viewPath(type, key, id))
}

export function toggleModuleGroup(modKey: string): void {
  const { expandedModule } = useNav()
  expandedModule.value = expandedModule.value === modKey ? null : modKey
}

export function toggleGroup(gid: string): void {
  const { expandedGroup } = useNav()
  expandedGroup.value = expandedGroup.value === gid ? null : gid
}

export function toggleSidebar(): void {
  const { navOpen, collapsed } = useNav()
  if (window.matchMedia('(max-width:900px)').matches) navOpen.value = !navOpen.value
  else collapsed.value = !collapsed.value
}

/** `viewTitle` de la maquette. */
export function viewTitle(view: View): string {
  if (view.type === 'dashboard') return 'Tableau de bord'
  if (view.type === 'guide') return 'Mode d\'emploi'
  if (view.type === 'module') return MODULES[view.key!]?.label.replace(/^\d+\.\s*/, '') ?? ''
  if (view.type === 'group') return MODULES[view.key!]?.groups.find(g => g.key === view.id)?.label ?? ''
  if (view.type === 'entity') return ENTITIES[view.key!]?.labelPlural ?? ''
  if (view.type === 'detail') return `${ENTITIES[view.key!]?.label}, détails`
  if (view.type === 'report') return REPORTS[view.key!]?.label ?? ''
  if (view.type === 'wizard') return 'Parcours de démonstration'
  if (view.type === 'relance') return 'Lettre de relance'
  if (view.type === 'relanceBulk') return 'Lettres de relance groupées'
  if (view.type === 'recu-inscription') return 'Reçu d\'inscription'
  if (view.type === 'attestation') return 'Attestation de solde'
  if (view.type === 'liste-classe') return 'Liste de classe'
  if (view.type === 'releve-notes') return 'Relevé de notes officiel'
  if (view.type === 'recu-print') return 'Reçu'
  return ''
}
